import authService from "../services/auth.service";

// Obtener usuario desde localStorage
const user = JSON.parse(localStorage.getItem("userF"));
const token = localStorage.getItem("auth_token");

// Estado inicial más completo
const initialState = {
  user: user && typeof user === 'object' ? user : null,
  token: token || null,
  loggedIn: !!(user && token),
};

export const auth = {
  namespaced: true,
  state: initialState,
  
  actions: {
    async login({ commit, dispatch }, userData) {
      try {
        const response = await authService.login(userData);
        
        if (response.data && response.data.token) {
          const { user, token } = response.data;
          
          if (user) {
            // Si obtenemos user y token del servicio
            commit("loginSuccess", { user, token });
            
            // Intentar cargar permisos después del login exitoso
            try {
              await dispatch('loadUserPermissions');
            } catch (error) {
              console.warn('No se pudieron cargar los permisos después del login');
            }
          } else {
            // Si solo obtenemos el token, marcar como logueado sin user info
            commit("loginWithTokenOnly", token);
            
            // Intentar obtener la info del usuario después
            try {
              await dispatch('refreshUserInfo');
              await dispatch('loadUserPermissions');
            } catch (error) {
              console.warn('No se pudo obtener información del usuario, pero el login fue exitoso');
            }
          }
        } else {
          // Fallback para compatibilidad
          commit("logMeIn");
        }
        
        return response;
      } catch (error) {
        commit("loginFailure");
        throw error;
      }
    },

    async logout({ commit }) {
      try {
        await authService.logout();
      } catch (error) {
        console.error("Logout error:", error);
      } finally {
        commit("logMeOut");
      }
    },

    async register({ commit }, userData) {
      try {
        const response = await authService.register(userData);
        
        if (response.data && response.data.user && response.data.token) {
          // Si obtenemos user y token del servicio
          const { user, token } = response.data;
          commit("loginSuccess", { user, token });
        } else {
          // Si solo obtenemos el token, marcar como logueado sin user info
          commit("logMeIn");
        }
        
        return response;
      } catch (error) {
        commit("loginFailure");
        throw error;
      }
    },

    // Acción para cargar usuario desde localStorage al inicializar
    loadUserFromStorage({ commit, dispatch }) {
      const user = JSON.parse(localStorage.getItem("userF"));
      const token = localStorage.getItem("auth_token");
      
      if (token) {
        if (user && typeof user === 'object') {
          // Si tenemos información completa del usuario
          commit("loginSuccess", { user, token });
        } else {
          // Si solo tenemos el token
          commit("logMeIn");
          // Intentar obtener info del usuario
          dispatch('refreshUserInfo');
        }
        
        // Cargar permisos si estamos logueados
        dispatch('loadUserPermissions');
      }
    },

    // Acción para refrescar información del usuario
    async refreshUserInfo({ commit, state }) {
      try {
        if (!state.token) {
          console.warn('No hay token disponible para refrescar información del usuario');
          return;
        }
        
        const userData = await authService.refreshUser();
        commit("updateUser", userData);
        return userData;
      } catch (error) {
        console.error("Error refreshing user info:", error);
        // Si el token es inválido, limpiar todo
        if (error.response && error.response.status === 401) {
          commit("logMeOut");
        }
        throw error;
      }
    },

    // Nueva acción para cargar permisos del usuario
    async loadUserPermissions({ commit, state }) {
      try {
        if (!state.token) {
          console.warn('No hay token disponible para cargar permisos');
          return;
        }
        
        const permissions = await authService.getUserPermissions();
        commit("setUserPermissions", permissions);
        return permissions;
      } catch (error) {
        console.error("Error loading user permissions:", error);
        // Si el token es inválido, limpiar todo
        if (error.response && error.response.status === 401) {
          commit("logMeOut");
        }
        throw error;
      }
    },

    // Acción para actualizar perfil del usuario
    async updateProfile({ commit }, profileData) {
      const response = await authService.updateProfile(profileData);
      const updatedUser = response.data.user || response.data;
      
      // Actualizar localStorage
      localStorage.setItem("userF", JSON.stringify(updatedUser));
      
      // Actualizar estado
      commit("updateUser", updatedUser);
      
      return response;
    },
  },

  mutations: {
    loginSuccess(state, { user, token }) {
      state.loggedIn = true;
      state.user = user;
      state.token = token;
    },

    loginFailure(state) {
      state.loggedIn = false;
      state.user = null;
      state.token = null;
    },

    logMeIn(state) {
      state.loggedIn = true;
    },

    loginWithTokenOnly(state, token) {
      state.loggedIn = true;
      state.user = null;
      state.token = token;
    },

    logMeOut(state) {
      state.loggedIn = false;
      state.user = null;
      state.token = null;
      // Limpiar localStorage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('userF');
    },

    updateUser(state, user) {
      state.user = { ...state.user, ...user };
      // Actualizar localStorage
      localStorage.setItem("userF", JSON.stringify(state.user));
    },

    setToken(state, token) {
      state.token = token;
      // Actualizar localStorage
      localStorage.setItem("auth_token", token);
    },

    // Nueva mutación para establecer permisos del usuario
    setUserPermissions(state, permissions) {
      if (state.user) {
        state.user = { ...state.user, permissions };
        // Actualizar localStorage
        localStorage.setItem("userF", JSON.stringify(state.user));
      }
    },
  },

  getters: {
    loggedIn(state) {
      return state.loggedIn;
    },

    user(state) {
      return state.user || {};
    },

    token(state) {
      return state.token;
    },

    userName(state) {
      return state.user?.name || '';
    },

    userEmail(state) {
      return state.user?.email || '';
    },

    userRole(state) {
      return state.user?.role || '';
    },

    userAvatar(state) {
      return state.user?.avatar || null;
    },

    userInitials(state) {
      if (!state.user?.name) return 'U';
      
      const names = state.user.name.split(' ');
      if (names.length === 1) {
        return names[0].charAt(0).toUpperCase();
      }
      
      return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
    },

    // Nuevos getters para permisos
    userPermissions(state) {
      return state.user?.permissions || {};
    },

    hasPermission: (state) => (permission) => {
      return state.user?.permissions?.[permission] === true;
    },

    // Getter específico para verificar acceso a módulos
    hasModuleAccess: (state) => (moduleId) => {
      const permissions = state.user?.permissions || {};
      return permissions[moduleId] === true;
    },
  },
};